import { ArrowRight, TrendingDown, TrendingUp, Euro, AlertCircle, Zap, Leaf, Banknote, Clock, Info } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import type { ScenarioWithImprovements } from '../../types/work-scenarios';

interface ScenarioSummaryProps {
  scenario: ScenarioWithImprovements;
  dpeData?: any;
}

const DPE_CLASS_COLORS: Record<string, string> = {
  A: 'bg-green-600',
  B: 'bg-green-500',
  C: 'bg-yellow-400',
  D: 'bg-yellow-500',
  E: 'bg-orange-500',
  F: 'bg-red-500',
  G: 'bg-red-700',
};

function DualDPELabel({
  energyClass,
  climateClass,
  label
}: {
  energyClass: string;
  climateClass: string;
  label: string;
}) {
  return (
    <div className="text-center">
      <div className="text-sm text-gray-600 mb-3 font-medium">{label}</div>
      <div className="flex flex-col gap-3">
        {/* Classe Énergie */}
        <div className="flex flex-col items-center">
          <div
            className={`${
              DPE_CLASS_COLORS[energyClass] || 'bg-gray-400'
            } text-white font-bold text-3xl py-3 px-6 rounded-lg shadow-md min-w-[80px]`}
          >
            {energyClass}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
            <Zap className="w-3.5 h-3.5" />
            <span>Énergie</span>
          </div>
        </div>

        {/* Classe GES */}
        <div className="flex flex-col items-center">
          <div
            className={`${
              DPE_CLASS_COLORS[climateClass] || 'bg-gray-400'
            } text-white font-bold text-3xl py-3 px-6 rounded-lg shadow-md min-w-[80px]`}
          >
            {climateClass}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
            <Leaf className="w-3.5 h-3.5" />
            <span>GES</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ScenarioSummary({ scenario, dpeData }: ScenarioSummaryProps) {
  const hasImprovements = scenario.improvements.length > 0;

  // Calculer la consommation avec l'ancien coefficient 2.3 pour affichage comparatif
  // La consommation stockée (current_energy_consumption) est déjà avec le coefficient 2026 (1.9)
  // Pour afficher la valeur avec l'ancien coefficient 2.3, on applique le ratio 2.3/1.9 = 1.21
  const currentConsumptionWith2026Coef = scenario.current_energy_consumption || null;
  const currentConsumptionWith2023Coef = currentConsumptionWith2026Coef
    ? Math.round(currentConsumptionWith2026Coef * (2.3 / 1.9))
    : null;

  // Vérifier si le DPE est 100% électrique (pour afficher le message de conversion)
  const isFullyElectric = dpeData?.type_energie_principale_chauffage === 'Électricité'
    && (dpeData?.type_energie_principale_ecs === 'Électricité' || !dpeData?.type_energie_principale_ecs);

  // Détecter si c'est un remplacement gaz → radiateurs électriques
  const hasNegativeEnergySavings = (scenario.total_energy_savings || 0) < 0;
  const hasHeatingReplacement = scenario.improvements.some(
    imp => imp.improvement_type === 'heating'
  );

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{scenario.name}</h2>
        {scenario.description && (
          <p className="mt-2 text-gray-600">{scenario.description}</p>
        )}
      </div>

      {hasImprovements ? (
        <>
          <div className="mb-6">
            <p className="text-sm text-center text-gray-500 mb-4">
              Classes énergétique (consommation) et climatique (émissions GES)
            </p>
            <div className="flex items-center justify-center gap-8">
              <DualDPELabel
                energyClass={scenario.current_energy_class || 'N/A'}
                climateClass={scenario.current_climate_class || 'N/A'}
                label="Actuel"
              />
              <ArrowRight className="w-8 h-8 text-gray-400 flex-shrink-0" />
              <DualDPELabel
                energyClass={scenario.projected_energy_class || 'N/A'}
                climateClass={scenario.projected_climate_class || 'N/A'}
                label="Projeté"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <TrendingDown className="w-5 h-5" />
                <div className="flex flex-col">
                  <span className="font-medium">Économies annuelles</span>
                  <span className="text-xs text-blue-500">(Énergie finale - Facture)</span>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {scenario.total_energy_savings?.toLocaleString() || 0} kWh/an
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <Euro className="w-5 h-5" />
                <span className="font-medium">Économies financières</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {scenario.total_cost_savings?.toLocaleString() || 0} €/an
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-orange-600 mb-2">
                <Banknote className="w-5 h-5" />
                <span className="font-medium">Investissement total</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {scenario.total_investment?.toLocaleString() || 0} €
              </div>
            </div>

            <div className="bg-teal-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-teal-600 mb-2">
                <Clock className="w-5 h-5" />
                <span className="font-medium">Temps de retour</span>
              </div>
              <div className={`text-2xl font-bold ${
                scenario.payback_period_years && scenario.payback_period_years < 10
                  ? 'text-green-700'
                  : scenario.payback_period_years && scenario.payback_period_years <= 20
                  ? 'text-orange-700'
                  : 'text-red-700'
              }`}>
                {scenario.payback_period_years && scenario.total_cost_savings && scenario.total_cost_savings > 0
                  ? `${scenario.payback_period_years.toFixed(1)} ans`
                  : 'N/A'}
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Consommation d'énergie primaire (DPE actuel)</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {currentConsumptionWith2026Coef || scenario.current_energy_consumption} kWh/m²/an
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-green-600">
                  {scenario.projected_energy_consumption} kWh/m²/an
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-600">Émissions de GES</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {scenario.current_ges_emissions} kgCO2/m²/an
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-green-600">
                  {scenario.projected_ges_emissions} kgCO2/m²/an
                </span>
              </div>
            </div>
          </div>

          {hasHeatingReplacement && (
            <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-blue-900 mb-2">
                    Comprendre les économies d'énergie
                  </div>
                  <p className="text-sm text-blue-800 mb-3">
                    Les <strong>économies affichées ({Math.abs(scenario.total_energy_savings || 0).toLocaleString()} kWh/an)</strong> correspondent à votre <strong>consommation d'énergie finale (EF)</strong> - c'est l\'énergie que vous consommez réellement et qui apparaît sur votre facture.
                  </p>
                  <p className="text-sm text-blue-800 mb-3">
                    Le <strong>DPE se base sur l'énergie primaire (EP)</strong>, qui applique un coefficient multiplicateur de <strong>1.9 pour l'électricité</strong> (au lieu de 1.0 pour le gaz). C'est pourquoi un remplacement gaz → électrique peut économiser en facture mais dégrader le DPE.
                  </p>
                  <div className="bg-white p-3 rounded border border-blue-200">
                    <p className="text-xs text-blue-700 font-medium mb-1">Exemple concret :</p>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>Énergie finale (facture) : Vous économisez réellement de l'énergie</li>
                      <li>Énergie primaire (DPE) : Votre classe peut se dégrader à cause du coefficient 1.9</li>
                      <li>Émissions GES : Elles diminuent grâce au mix électrique français peu carboné</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isFullyElectric && currentConsumptionWith2023Coef && currentConsumptionWith2026Coef && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-blue-900 mb-2">
                    Information : Nouveaux facteurs 2026
                  </div>
                  <p className="text-sm text-blue-800 mb-3">
                    Depuis le 1er janvier 2026, le coefficient de conversion de l'électricité est passé de 2,3 à 1,9 et le facteur d'émissions GES de l'électricité de 0,079 à 0,065 kgCO2/kWh.
                    <strong> Les lettres DPE et les émissions GES affichées ci-dessus intègrent déjà ces nouveaux facteurs ainsi que l'ajustement pour les petites surfaces.</strong>
                  </p>
                  <p className="text-sm text-blue-700 italic">
                    Consommation DPE d'origine (coefficient 2,3) : {currentConsumptionWith2023Coef} kWh/m²/an → Avec coefficient 2026 (1,9) : {currentConsumptionWith2026Coef} kWh/m²/an
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-6">
          <p className="text-gray-500">
            Aucun travaux planifié pour l'instant.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Ajoutez des travaux pour voir les économies potentielles
          </p>
        </div>
      )}
    </Card>
  );
}
